import { useState, useEffect, useMemo, useCallback, useContext, useRef } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Skeleton } from "primereact/skeleton";
import { BiSearch } from "react-icons/bi";
import { useNavigate, useOutletContext } from "react-router-dom";
import { InputSwitch } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";
import CourseService from "../../../services/CourseService";
import DocumentService from "../../../services/DocumentService";
import DocumentTypeService from "../../../services/DocumentTypeService";
import ConfirmDialogBox from "../../confirmDialog/ConfirmDialogBox";
import { Dropdown } from "primereact/dropdown";

const DocumentMapping = () => {
  const { filters } = useOutletContext();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [pendingCourse, setPendingCourse] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [initialRightItems, setInitialRightItems] = useState([]);
  const submitControllerRef = useRef(null);
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");
  const [selectAllLeft, setSelectAllLeft] = useState(false);
  const [selectAllRight, setSelectAllRight] = useState(false);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [assigned, setAssigned] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [statusChanged, setStatusChanged] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
  const documentAccess = authContext.getPermissions("documents") || [];

  if (!documentAccess.some(f => f.access.includes(1))) {
    navigate("/");
  }

  setShowAdd(documentAccess.some(f => f.access.includes(2)));
  setShowEdit(documentAccess.some(f => f.access.includes(3)));
}, [navigate, authContext]);

const loadCourses = useCallback(async (signal) => { 
    setLoadingCourses(true);
    try {
      const data = await CourseService.GetAll(authContext, signal);

      if (signal?.aborted) return;

      if (data.isSuccess) {
        setCourses(data.data);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      AlertService.error("Failed to load courses");
    } finally {
      if (!signal?.aborted) setLoadingCourses(false);
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController();
    loadCourses(controller.signal);
    return () => controller.abort();
  }, [loadCourses]);

const fetchDocumentTags = useCallback(async (signal) => {
    try {
      const res = await DocumentTypeService.GetDocumentTags(
        authContext,
        signal
      );

      if (signal?.aborted) return; 

      if (res?.isSuccess && Array.isArray(res.data)) {
        setCategories(["All", ...res.data.map((m) => m.label)]);
      } else {
        setCategories(["All"]);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      setCategories(["All"]);
      AlertService.error("Failed to fetch document tags");
    }
  }, [authContext]);

const loadCourseDocuments = useCallback(async (courseId, signal) => { 
    if (!courseId) return;
    setLoadingDocs(true);
    try {
      const [docData, attachData] = await Promise.all([
        DocumentService.GetAll("", authContext, signal),
        CourseService.GetAttachments(courseId, "", authContext, signal),
      ]);

      if (signal?.aborted) return;

      if (!docData.isSuccess) {
        AlertService.error(docData.errorMessage);
        return;
      }

      if (attachData.isSuccess) {
        const attachedDocIds = attachData.data.map((m) => m.documentId);
        setLeftItems(
          docData.data
            .filter((f) => !attachedDocIds.includes(f.id))
            .map((m) => ({
              id: m.id,
              label: m.documentName,
              checked: false,
              category: m.taggedUnderDesc || "Uncategorized",
            }))
        );
        const newRight = attachData.data.map((m) => ({
          id: m.documentId,
          label: m.documentName,
          checked: false,
          category: m.documentTag || "Uncategorized",
          status: m.status,
        }));
        setRightItems(newRight);
        setInitialRightItems(newRight.map((i) => ({ ...i })));
      } else {
        setLeftItems(
          docData.data.map((m) => ({
            id: m.id,
            label: m.documentName,
            checked: false,
            category: m.taggedUnderDesc || "Uncategorized",
          }))
        );
        setRightItems([]);
        setInitialRightItems([]);
        AlertService.error(attachData.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
      AlertService.error("Failed to load documents");
    } finally {
      if (!signal?.aborted) setLoadingDocs(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    fetchDocumentTags(controller.signal);
    return () => controller.abort(); 
  }, [fetchDocumentTags]);

useEffect(() => {
    const controller = new AbortController(); 
    if (selectedCourse) {
      loadCourseDocuments(selectedCourse.id, controller.signal); 
    } else {
      setLeftItems([]);
      setRightItems([]);
      setInitialRightItems([]);
    }
    return () => controller.abort();
  }, [selectedCourse, loadCourseDocuments]);
  
  useEffect(() => {
    const currentIds = new Set(rightItems.map((i) => i.id));

    setAssigned(
      rightItems.filter(
        (item) => !initialRightItems.some((init) => init.id === item.id),
      ),
    );
    setRemoved(initialRightItems.filter((init) => !currentIds.has(init.id)));
    setStatusChanged(
      rightItems.filter((item) => {
        const init = initialRightItems.find((i) => i.id === item.id);
        return init && init.status !== item.status;
      }),
    );
  }, [rightItems, initialRightItems]);

  const hasChanges = useMemo(
    () => assigned.length > 0 || removed.length > 0 || statusChanged.length > 0,
    [assigned, removed, statusChanged],
  );

  //Filtering
  const activeFilters = Object.keys(filters).filter((cat) => filters[cat]);

  const filteredLeftItems = useMemo(() => {
    if (activeFilters.length === 0 || activeFilters.includes("All"))
      return leftItems;
    return leftItems.filter((item) => activeFilters.includes(item.category));
  }, [leftItems, activeFilters]);

  const filteredRightItems = useMemo(() => {
    if (activeFilters.length === 0 || activeFilters.includes("All"))
      return rightItems;
    return rightItems.filter((item) => activeFilters.includes(item.category));
  }, [rightItems, activeFilters]);

  const groupedLeftItems = useMemo(() => {
    const groups = {};
    categories
      .filter((cat) => cat !== "All")
      .forEach((category) => {
        groups[category] = filteredLeftItems.filter(
          (item) =>
            item.category === category &&
            item.label.toLowerCase().includes(searchLeft.toLowerCase()),
        );
      });
    return groups;
  }, [filteredLeftItems, categories, searchLeft]);

  const groupedRightItems = useMemo(() => {
    const groups = {};
    categories
      .filter((cat) => cat !== "All")
      .forEach((category) => {
        groups[category] = filteredRightItems.filter(
          (item) =>
            item.category === category &&
            item.label.toLowerCase().includes(searchRight.toLowerCase()),
        );
      });
    return groups;
  }, [filteredRightItems, categories, searchRight]);

  // Handlers 
  const handleCategoryCheckLeft = (category, checked) => {
    setLeftItems((prev) =>
      prev.map((item) =>
        category === "All" || item.category === category
          ? { ...item, checked }
          : item,
      ),
    );
  };

  const handleCategoryCheckRight = (category, checked) => {
    setRightItems((prev) =>
      prev.map((item) =>
        category === "All" || item.category === category
          ? { ...item, checked }
          : item,
      ),
    );
  };

  const handleMove = (direction) => {
    if (direction === "right") {
      const selected = leftItems.filter((i) => i.checked);
      setRightItems((prev) => [
        ...prev,
        ...selected.map((i) => ({ ...i, checked: false, status: true })),
      ]);
      setLeftItems((prev) => prev.filter((i) => !i.checked));
    } else {
      const selected = rightItems.filter((i) => i.checked);
      setLeftItems((prev) => [
        ...prev,
        ...selected.map((i) => ({
          id: i.id,
          label: i.label,
          checked: false,
          category: i.category,
        })),
      ]);
      setRightItems((prev) => prev.filter((i) => !i.checked));
    }
  };

  const toggleSelectAll = (panel) => {
    if (panel === "left") {
      const newVal = !selectAllLeft;
      setSelectAllLeft(newVal);
      setLeftItems((prev) => prev.map((i) => ({ ...i, checked: newVal })));
    } else {
      const newVal = !selectAllRight;
      setSelectAllRight(newVal);
      setRightItems((prev) => prev.map((i) => ({ ...i, checked: newVal })));
    }
  };

  const handleToggleStatus = useCallback((id) => {
    setRightItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: !item.status } : item,
      ),
    );
  }, []);

  useEffect(() => {
    setSelectAllLeft(leftItems.length > 0 && leftItems.every((i) => i.checked));
  }, [leftItems]);

  useEffect(() => {
    setSelectAllRight(
      rightItems.length > 0 && rightItems.every((i) => i.checked),
    );
  }, [rightItems]);

  const handleCancel = () => navigate("/document-dashboard/documents");

  const handlePreviewClick = (e) => {
    e.preventDefault();
    if (!hasChanges) return;
    setShowChangesModal(true);
  };



const confirmSubmit = async () => {
  setShowChangesModal(false);

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  const payload = rightItems.map((item) => ({
    courseId: parseInt(selectedCourse.id),
    documentId: parseInt(item.id),
    status: item.status,
  }));

  try {
    const result = await CourseService.SaveAttachment(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (result.isSuccess) {
      AlertService.success("Document assignments updated successfully");
      await loadCourseDocuments(selectedCourse.id);
    } else {
      AlertService.error(result.errorMessage || "Failed to save");
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("Save failed");
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  const renderSkeletonCategory = (isRightPanel = false) => (
    <div className="mb-2">
      <div className="d-flex align-items-center pb-2 pt-2 ps-3 border-bottom">
        <Skeleton shape="circle" size="1.5rem" className="me-2" />
        <Skeleton width="10rem" height="1.2rem" />
      </div>
      {[...Array(7)].map((_, i) => (
        <div
          key={i}
          className="d-flex align-items-center pb-2 pt-2 ps-3 border-bottom"
          style={{
            justifyContent: isRightPanel ? "space-between" : "flex-start",
          }}
        >
          <div className="d-flex align-items-center">
            <Skeleton shape="circle" size="1.5rem" className="ms-4" />
            <Skeleton width="40rem" height="1.2rem" className="ms-2" />
          </div>
        </div>
      ))}
    </div>
  );

  const modalFooter = (
    <div className="pt-2">
      <button
        className="btn btn-outline-secondary px-5 py-2 me-4"
        onClick={() => setShowChangesModal(false)}
      >
        Cancel
      </button>
       {(showAdd || showEdit) && (
      <button className="btn btn-primary px-5 py-2" onClick={confirmSubmit}>
        Submit
      </button>
          )}
    </div>
  );

  return (
    <div className="container-fluid custom-container">
      {/* Course selector */}
   <div className="d-flex justify-content-end mb-2 pt-1">
  {loadingCourses ? (
    <Skeleton width="10rem" height="2.2rem" />
  ) : (
    <Dropdown
      value={selectedCourse?.id ?? null}
      onChange={(e) => {
        const selected = courses.find((c) => c.id === e.value);
        if (!selected) return;
        if (selectedCourse?.id === selected.id) return;

        setPendingCourse(selected);
        setShowConfirmDialog(true);
      }}
      options={courses}
      optionLabel="courseName"
      optionValue="id"
      placeholder="Select Course"
      filter
      filterPlaceholder="Search course..."
      className="w-100 custom-input-color select-dropdown"
      panelClassName="select-dropdown-panel"
      emptyMessage="No courses found"
      emptyFilterMessage="No matching course found"
      style={{ minWidth: "220px" , maxWidth:"300px"}}
    />
  )}
</div>

      <div className="row g-1">
        {/*Left Panel - Master's Repository */}
        <div
          className="col-12 col-md border rounded card p-0"
          style={{ minWidth: "300px" }}
        >
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom bg-section rounded-3">
            <div className="d-flex align-items-center">
              {loadingDocs ? (
                <Skeleton shape="circle" size="1.5rem" className="me-2" />
              ) : (
                <Checkbox
                  checked={selectAllLeft}
                  onChange={() => toggleSelectAll("left")}
                  className="me-2"
                />
              )}
              <span className="fw-bold">Master's Repository</span>
            </div>
            <div className="position-relative">
              {loadingDocs ? (
                <Skeleton width="10rem" height="2rem" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchLeft}
                    onChange={(e) => setSearchLeft(e.target.value)}
                    disabled={!selectedCourse}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>

          <div
            className="pt-1 scroll-no-buttons"
            style={{ height: "660px", overflowY: "auto" }}
          >
            {!selectedCourse ? (
              <div className="text-center p-5 text-muted">
                Please select a course
              </div>
            ) : loadingDocs ? (
              <>
                {renderSkeletonCategory()}
                {renderSkeletonCategory()}
              </>
            ) : (
              categories
                .filter((cat) => cat !== "All")
                .map(
                  (category) =>
                    groupedLeftItems[category]?.length > 0 && (
                      <div key={`left-${category}`} className="mb-0">
                        <div
                          className="d-flex align-items-center pb-3 pt-3 ps-3 border-bottom"
                          style={{ cursor: "pointer" }}
                        >
                          <Checkbox
                            checked={groupedLeftItems[category].every(
                              (i) => i.checked,
                            )}
                            onChange={(e) =>
                              handleCategoryCheckLeft(category, e.checked)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="ms-2 fw-bold">{category}</span>
                        </div>
                        {groupedLeftItems[category].map((item) => (
                          <div
                            key={item.id}
                            className="d-flex align-items-center pb-2 pt-2 px-3 border-bottom"
                            style={{ minHeight: "56px" }}
                          >
                            <Checkbox
                              className="ms-4 flex-shrink-0"
                              checked={item.checked}
                              onChange={(e) =>
                                setLeftItems((current) =>
                                  current.map((i) =>
                                    i.id === item.id
                                      ? { ...i, checked: e.checked }
                                      : i,
                                  ),
                                )
                              }
                            />
                            <span
                              className="ms-3 text-break hyphens-auto flex-grow-1"
                              style={{
                                wordBreak: "break-word",
                                lineHeight: "1.4",
                                overflowWrap: "break-word",
                              }}
                            >
                              {item.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    ),
                )
            )}
          </div>
        </div>

        {/* Middle buttons */}
        <div className="col-12 col-md-2 col-lg-1 d-flex flex-md-column justify-content-center align-items-center gap-3">
                   {(showAdd || showEdit) && (
                          <>
          <Button
            icon="pi pi-arrow-right"
            className="btn border-0 fw-bold"
            style={{ backgroundColor: "#9CF", minWidth: "60px" }}
            onClick={() => handleMove("right")}
            disabled={!leftItems.some((i) => i.checked) || !selectedCourse}
          />
          <Button
            icon="pi pi-arrow-left"
            className="btn border-0 fw-bold"
            style={{ backgroundColor: "#9CF", minWidth: "60px" }}
            onClick={() => handleMove("left")}
            disabled={!rightItems.some((i) => i.checked) || !selectedCourse}
          />
          </>
                      )}
        </div>

        {/* Right Panel - Selected Repository */}
        <div
          className="col-12 col-md border rounded card p-0"
          style={{ minWidth: "300px" }}
        >
          <div className="d-flex justify-content-between align-items-center p-2 ps-3 border-bottom bg-section rounded-3">
            <div className="d-flex align-items-center">
              {loadingDocs ? (
                <Skeleton shape="circle" size="1.5rem" className="me-2" />
              ) : (
                <Checkbox
                  checked={selectAllRight}
                  onChange={() => toggleSelectAll("right")}
                  className="me-2"
                />
              )}
              <span className="fw-bold">Selected Repository</span>
            </div>
            <div className="position-relative">
              {loadingDocs ? (
                <Skeleton width="10rem" height="2rem" />
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control custom-input-color"
                    placeholder="Search..."
                    value={searchRight}
                    onChange={(e) => setSearchRight(e.target.value)}
                    disabled={!selectedCourse}
                  />
                  <BiSearch className="position-absolute top-50 end-0 translate-middle-y me-2 text-muted" />
                </>
              )}
            </div>
          </div>

          <div
            className="pt-1 scroll-no-buttons"
            style={{ height: "660px", overflowY: "auto" }}
          >
            {!selectedCourse ? (
              <div className="text-center p-5 text-muted">
                Please select a course
              </div>
            ) : loadingDocs ? (
              <>
                {renderSkeletonCategory(true)}
                {renderSkeletonCategory(true)}
              </>
            ) : (
              categories
                .filter((cat) => cat !== "All")
                .map(
                  (category) =>
                    groupedRightItems[category]?.length > 0 && (
                      <div key={`right-${category}`} className="mb-0">
                        <div
                          className="d-flex align-items-center pb-3 pt-3 ps-3 border-bottom"
                          style={{ cursor: "pointer" }}
                        >
                          <Checkbox
                            checked={groupedRightItems[category].every(
                              (i) => i.checked,
                            )}
                            onChange={(e) =>
                              handleCategoryCheckRight(category, e.checked)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="ms-2 fw-bold">{category}</span>
                        </div>
                        {groupedRightItems[category].map((item) => (
                          <div
                            key={item.id}
                            className="d-flex align-items-center pb-2 pt-2 px-3 border-bottom position-relative"
                            style={{ minHeight: "56px" }}
                          >
                            <div className="d-flex align-items-center flex-grow-1 pe-5">
                              <Checkbox
                                className="ms-4 flex-shrink-0"
                                checked={item.checked}
                                onChange={(e) =>
                                  setRightItems((current) =>
                                    current.map((i) =>
                                      i.id === item.id
                                        ? { ...i, checked: e.checked }
                                        : i,
                                    ),
                                  )
                                }
                              />
                              <span
                                className="ms-3 text-break hyphens-auto"
                                style={{
                                  wordBreak: "break-word",
                                  lineHeight: "1.4",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {item.label}
                              </span>
                            </div>
                            <div className="position-absolute end-0 top-50 translate-middle-y pe-3">
                              <InputSwitch
                                checked={item.status}
                                onChange={() => handleToggleStatus(item.id)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ),
                )
            )}
          </div>
        </div>
      </div>

      {/*Action buttons*/}
      <div className="row mt-3">
        <div className="col-12 d-flex justify-content-end gap-4">
          <Button
            label="Cancel"
            className="btn btn-outline px-5"
            onClick={handleCancel}
          />

          <Button
            label="Preview"
            className="btn btn-primary py-2 px-5"
            onClick={handlePreviewClick}
            disabled={!hasChanges || !selectedCourse}
          />
        </div>
      </div>

      {/*Preview Modal */}
      <Dialog
        header={<div className="w-100 text-center">Preview</div>}
        visible={showChangesModal}
        style={{ width: "40vw", height: "70vh" }}
        contentStyle={{ maxHeight: "60vh", overflowY: "auto" }}
        footer={modalFooter}
        onHide={() => setShowChangesModal(false)}
      >
        <div className="mb-4">
          <h5 className="mb-3">
            <strong>Course Name</strong>: {selectedCourse?.courseName}
          </h5>

          {assigned.length > 0 && (
            <div className="mb-4">
              <h5>Added to Selected Repository</h5>
              <ul className="list-group">
                {assigned.map((item) => (
                  <li
                    key={item.id}
                    className="list-group-item text-primary d-flex justify-content-between align-items-center"
                  >
                    <span>{item.label}</span>
                    <span
                      className={`badge ${item.status ? "bg-success" : "bg-danger"}`}
                    >
                      {item.status ? "Enable" : "Disable"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {removed.length > 0 && (
            <div className="mb-4">
              <h5>Removed to Master's Repository</h5>
              <ul className="list-group">
                {removed.map((item) => (
                  <li key={item.id} className="list-group-item text-primary">
                    {item.label}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {statusChanged.length > 0 && (
            <div>
              <h5>Status Changes</h5>
              <ul className="list-group">
                {statusChanged.map((item) => (
                  <li
                    key={item.id}
                    className="list-group-item text-primary d-flex justify-content-between align-items-center"
                  >
                    <span>{item.label}</span>
                    <span
                      className={`badge ${item.status ? "bg-success" : "bg-danger"}`}
                    >
                      {item.status ? "Enable" : "Disable"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Dialog>

      {/*Confirm Diolog*/}
      <ConfirmDialogBox
        visible={showConfirmDialog}
        onHide={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          setSelectedCourse(pendingCourse);
          setShowConfirmDialog(false);
        }}
        title={
          <>
            Are you sure you want to select {""}
            <strong>{pendingCourse?.courseName}</strong>?
          </>
        }
        confirmLabel="Yes, Confirm"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default DocumentMapping;
